import Config from '../classes/Config';
import PlayGame from './PlayGame';
import GameButton from '../classes/GameButton';
import PopInText from '../classes/PopInText';
import { Dialog } from '@capacitor/dialog';

export default class Friends extends Phaser.Scene {
  constructor() {
    super({
      key: 'Friends'
    });
  }

  panels = {};
  buttons = {};
  activeTab = 'friends';
  friendCode = '';
  friendsList = [];
  incomingRequests = [];
  outgoingRequests = [];
  searchResults = [];
  friendsTables = [];

  getWidth() {
    return this.cameras.main.width;
  }

  getHeight() {
    return this.cameras.main.height;
  }

  preload() {}

  async create() {
    this.game.dnrConfig = new Config();
    this.contentContainer = null;

    const background = new Phaser.GameObjects.Graphics(this);
    background.fillStyle(0x00a6b1, 1);
    background.fillRoundedRect(0, 0, this.getWidth(), this.getHeight(), 3);
    background.setAlpha(0.94);
    this.add.existing(background);

    background
      .setInteractive(
        new Phaser.Geom.Rectangle(0, 0, this.getWidth(), this.getHeight()),
        Phaser.Geom.Rectangle.Contains
      )
      .on('pointerdown', () => {});

    let xChain = 50;
    const yChain = 45;
    const font = '32px Arial';

    this.buttons.myFriends = new GameButton({
      scene: this,
      x: xChain,
      y: yChain,
      w: 240,
      h: 85,
      depth: this.depth + 1,
      text: 'My Friends',
      font,
      radius: 22,
      borderWidth: 4,
      clickFunction: () => {
        this.switchTab('friends');
      },
      clickFunctionData: {}
    });

    xChain += 265;
    this.buttons.addFriend = new GameButton({
      scene: this,
      x: xChain,
      y: yChain,
      w: 240,
      h: 85,
      depth: this.depth + 1,
      text: 'Add Friend',
      font,
      radius: 22,
      borderWidth: 4,
      clickFunction: () => {
        this.switchTab('add');
      },
      clickFunctionData: {}
    });

    xChain += 265;
    this.buttons.requests = new GameButton({
      scene: this,
      x: xChain,
      y: yChain,
      w: 240,
      h: 85,
      depth: this.depth + 1,
      text: 'Requests',
      font,
      radius: 22,
      borderWidth: 4,
      clickFunction: () => {
        this.switchTab('requests');
      },
      clickFunctionData: {}
    });

    xChain += 265;
    this.buttons.openTables = new GameButton({
      scene: this,
      x: xChain,
      y: yChain,
      w: 250,
      h: 85,
      depth: this.depth + 1,
      text: 'Friend Games',
      font,
      radius: 22,
      borderWidth: 4,
      clickFunction: () => {
        this.switchTab('tables');
      },
      clickFunctionData: {}
    });

    this.buttons.close = new GameButton({
      scene: this,
      x: this.getWidth() - 70,
      y: yChain,
      w: 50,
      h: 50,
      depth: this.depth + 1,
      text: '✖',
      font: '40px Arial',
      radius: 20,
      borderWidth: 4,
      clickFunction: () => {
        this.closeScene();
      },
      clickFunctionData: {}
    });

    this.contentContainer = this.add.container(0, 0);
    await this.refreshData();
    this.switchTab('friends');
  }

  closeScene() {
    this.input.topOnly = true;
    this.scene.pause('Friends');
    this.scene.setVisible(false, 'Friends');
    this.scene.stop('Friends');
  }

  showToast(message) {
    const pit = new PopInText({
      scene: this,
      x: this.getWidth() / 2,
      y: this.getHeight() / 2,
      text: message,
      box: { w: 800, h: 200, radius: 24 },
      startScale: 0.65,
      endScale: 0.75,
      scaleBy: 0.95,
      duration: 180,
      font: '48px Arial Black',
      onComplete: () => {
        pit.closeTextUpdater({ runForSeconds: 2 });
      }
    });
  }

  async refreshData() {
    try {
      const data = await this.game.friendsListFromUDS();
      if (data) {
        this.friendCode = data.friendCode || '';
        this.friendsList = data.friends || [];
        this.incomingRequests = data.incomingRequests || [];
        this.outgoingRequests = data.outgoingRequests || [];
      }
      const tablesData = await this.game.getFriendsOpenTables();
      this.friendsTables = tablesData || [];

      const reqCount = this.incomingRequests.length;
      if (this.buttons.requests && this.buttons.requests.text) {
        this.buttons.requests.text.setText(reqCount > 0 ? `Requests (${reqCount})` : 'Requests');
      }
      const tblCount = this.friendsTables.length;
      if (this.buttons.openTables && this.buttons.openTables.text) {
        this.buttons.openTables.text.setText(tblCount > 0 ? `Friend Games (${tblCount})` : 'Friend Games');
      }
    } catch (e) {
      console.warn('Error refreshing friends data:', e);
    }
  }

  clearContent() {
    if (this.activeScrollPanel) {
      this.activeScrollPanel.destroy();
      this.activeScrollPanel = null;
    }
    if (this.contentContainer) {
      this.contentContainer.removeAll(true);
    }
  }

  switchTab(tab) {
    this.activeTab = tab;
    this.buttons.myFriends.unselect();
    this.buttons.addFriend.unselect();
    this.buttons.requests.unselect();
    this.buttons.openTables.unselect();

    if (tab === 'friends') this.buttons.myFriends.select();
    else if (tab === 'add') this.buttons.addFriend.select();
    else if (tab === 'requests') this.buttons.requests.select();
    else if (tab === 'tables') this.buttons.openTables.select();

    this.renderActiveTab();
  }

  renderActiveTab() {
    this.clearContent();
    const width = this.getWidth() - 100;

    // Friend Code banner at top of all tabs
    const codeBannerBg = this.add.graphics();
    codeBannerBg.fillStyle(0xffffff, 0.95);
    codeBannerBg.fillRoundedRect(50, 145, width, 75, 14);
    this.contentContainer.add(codeBannerBg);

    const codeLabel = this.add.text(
      75,
      182,
      `Your Friend Code:  ${this.friendCode || '...'}`,
      { font: '32px Arial Black', color: '#111111' }
    ).setOrigin(0, 0.5);
    this.contentContainer.add(codeLabel);

    const copyBtn = new GameButton({
      scene: this,
      x: this.getWidth() - 310,
      y: 155,
      w: 240,
      h: 55,
      text: 'Copy Code',
      font: '26px Arial',
      radius: 14,
      borderWidth: 2,
      addToScene: false,
      parentContainer: this.contentContainer,
      clickFunction: async () => {
        try {
          if (navigator.clipboard && this.friendCode) {
            await navigator.clipboard.writeText(this.friendCode);
            this.showToast(`Copied Friend Code: ${this.friendCode}`);
          } else {
            await Dialog.alert({ title: 'Your Friend Code', message: this.friendCode });
          }
        } catch {
          await Dialog.alert({ title: 'Your Friend Code', message: this.friendCode });
        }
      },
      clickFunctionData: {}
    });

    if (this.activeTab === 'friends') {
      this.renderMyFriendsTab(width);
    } else if (this.activeTab === 'add') {
      this.renderAddFriendTab(width);
    } else if (this.activeTab === 'requests') {
      this.renderRequestsTab(width);
    } else if (this.activeTab === 'tables') {
      this.renderFriendsTablesTab(width);
    }
  }

  renderMyFriendsTab(width) {
    if (this.friendsList.length === 0) {
      const panelBg = this.add.graphics();
      panelBg.fillStyle(0xffffff, 0.95);
      panelBg.fillRoundedRect(50, 235, width, 360, 14);
      this.contentContainer.add(panelBg);

      const emptyText = this.add.text(
        this.getWidth() / 2,
        380,
        'You have not added any friends yet.\nUse "Add Friend" to search by Display Name or Friend Code!',
        { font: '30px Arial', color: '#333333', align: 'center' }
      ).setOrigin(0.5);
      this.contentContainer.add(emptyText);

      const addNowBtn = new GameButton({
        scene: this,
        x: this.getWidth() / 2 - 150,
        y: 460,
        w: 300,
        h: 75,
        text: 'Add a Friend',
        font: '32px Arial',
        radius: 18,
        borderWidth: 3,
        addToScene: false,
        parentContainer: this.contentContainer,
        clickFunction: () => this.switchTab('add'),
        clickFunctionData: {}
      });
      return;
    }

    const items = this.friendsList.map((f) => ({
      dnrId: f.dnrId,
      avatar: this.game.getAvatarObjFromAvatarKey(f.avatar).avatar,
      label: `${f.name}\nLvl ${f.level} (${f.friendCode})`,
      rawName: f.name
    }));

    this.activeScrollPanel = this.createGridPanel({
      items,
      x: 50,
      y: 235,
      width,
      height: this.getHeight() - 275,
      titleText: `Friends (${items.length}) — Tap a friend to manage`,
      onChildClick: async (child) => {
        const friend = this.friendsList.find((f) => f.dnrId === child.name);
        if (!friend) return;
        const openTbl = this.friendsTables.find(
          (t) => t.host_id === friend.dnrId || (t.friend_names || []).includes(friend.name)
        );
        if (openTbl) {
          const { value } = await Dialog.confirm({
            title: friend.name,
            message: `${friend.name} is at an open table (${openTbl.id}). Join their game?`
          });
          if (value) {
            this.joinTableAndStart(openTbl.id);
            return;
          }
        }
        const { value: confirmRemove } = await Dialog.confirm({
          title: `Remove ${friend.name}?`,
          message: `Do you want to remove ${friend.name} (${friend.friendCode}) from your friends list?`
        });
        if (confirmRemove) {
          await this.game.removeFriendOnUDS(friend.dnrId);
          await this.refreshData();
          this.renderActiveTab();
          this.showToast(`Removed ${friend.name}`);
        }
      }
    });
  }

  renderAddFriendTab(width) {
    const enterCodeBtn = new GameButton({
      scene: this,
      x: 50,
      y: 235,
      w: 340,
      h: 75,
      text: 'Add by Friend Code',
      font: '30px Arial',
      radius: 16,
      borderWidth: 3,
      addToScene: false,
      parentContainer: this.contentContainer,
      clickFunction: async () => {
        const { value, cancelled } = await Dialog.prompt({
          title: 'Add by Friend Code',
          message: "Enter your friend's 6-character Friend Code:",
          inputText: ''
        });
        if (!cancelled && value && value.trim()) {
          const res = await this.game.sendFriendRequestToUDS({ friendCode: value.trim() });
          await this.refreshData();
          this.showToast(res?.message || res?.error || 'Request processed');
          if (this.activeTab === 'add') this.renderActiveTab();
        }
      },
      clickFunctionData: {}
    });

    const searchNameBtn = new GameButton({
      scene: this,
      x: 415,
      y: 235,
      w: 340,
      h: 75,
      text: 'Search by Name',
      font: '30px Arial',
      radius: 16,
      borderWidth: 3,
      addToScene: false,
      parentContainer: this.contentContainer,
      clickFunction: async () => {
        const { value, cancelled } = await Dialog.prompt({
          title: 'Search Players',
          message: 'Enter player Display Name or Friend Code:',
          inputText: ''
        });
        if (!cancelled && value && value.trim()) {
          const results = await this.game.searchFriendsOnUDS(value.trim());
          this.searchResults = results || [];
          this.renderActiveTab();
        }
      },
      clickFunctionData: {}
    });

    if (this.searchResults.length === 0) {
      const panelBg = this.add.graphics();
      panelBg.fillStyle(0xffffff, 0.95);
      panelBg.fillRoundedRect(50, 325, width, 300, 14);
      this.contentContainer.add(panelBg);

      const hint = this.add.text(
        this.getWidth() / 2,
        475,
        'Tap "Add by Friend Code" to enter a 6-character code,\nor tap "Search by Name" to find players by Display Name.',
        { font: '28px Arial', color: '#333333', align: 'center' }
      ).setOrigin(0.5);
      this.contentContainer.add(hint);
      return;
    }

    const items = this.searchResults.map((r) => {
      let statusTag = 'Tap to Add';
      if (r.friendshipStatus === 'accepted') statusTag = 'Friends ✓';
      else if (r.friendshipStatus === 'pending') statusTag = 'Pending...';
      return {
        dnrId: r.dnrId,
        avatar: this.game.getAvatarObjFromAvatarKey(r.avatar).avatar,
        label: `${r.name} (${r.friendCode})\n${statusTag}`,
        status: r.friendshipStatus
      };
    });

    this.activeScrollPanel = this.createGridPanel({
      items,
      x: 50,
      y: 325,
      width,
      height: this.getHeight() - 365,
      titleText: `Search Results (${items.length}) — Tap a player to send Friend Request`,
      onChildClick: async (child) => {
        const target = this.searchResults.find((r) => r.dnrId === child.name);
        if (!target) return;
        if (target.friendshipStatus === 'accepted') {
          this.showToast(`You are already friends with ${target.name}`);
          return;
        }
        const res = await this.game.sendFriendRequestToUDS({ targetDnrId: target.dnrId });
        target.friendshipStatus = res?.status || 'pending';
        await this.refreshData();
        this.renderActiveTab();
        this.showToast(res?.message || `Sent request to ${target.name}`);
      }
    });
  }

  renderRequestsTab(width) {
    const combined = [
      ...this.incomingRequests.map((r) => ({
        dnrId: r.dnrId,
        avatar: this.game.getAvatarObjFromAvatarKey(r.avatar).avatar,
        label: `${r.name} (${r.friendCode})\nIncoming — Tap to Accept`,
        direction: 'incoming',
        rawName: r.name
      })),
      ...this.outgoingRequests.map((r) => ({
        dnrId: r.dnrId,
        avatar: this.game.getAvatarObjFromAvatarKey(r.avatar).avatar,
        label: `${r.name} (${r.friendCode})\nSent (Pending)`,
        direction: 'outgoing',
        rawName: r.name
      }))
    ];

    if (combined.length === 0) {
      const panelBg = this.add.graphics();
      panelBg.fillStyle(0xffffff, 0.95);
      panelBg.fillRoundedRect(50, 235, width, 340, 14);
      this.contentContainer.add(panelBg);

      const emptyText = this.add.text(
        this.getWidth() / 2,
        405,
        'No pending friend requests right now.',
        { font: '30px Arial', color: '#333333', align: 'center' }
      ).setOrigin(0.5);
      this.contentContainer.add(emptyText);
      return;
    }

    this.activeScrollPanel = this.createGridPanel({
      items: combined,
      x: 50,
      y: 235,
      width,
      height: this.getHeight() - 275,
      titleText: `Friend Requests (${combined.length})`,
      onChildClick: async (child) => {
        const item = combined.find((c) => c.dnrId === child.name);
        if (!item) return;
        if (item.direction === 'incoming') {
          const { value } = await Dialog.confirm({
            title: 'Friend Request',
            message: `Accept friend request from ${item.rawName}? (Cancel to decline)`
          });
          await this.game.respondFriendRequestOnUDS(item.dnrId, value ? 'accept' : 'decline');
          await this.refreshData();
          this.renderActiveTab();
          this.showToast(value ? `Added ${item.rawName}!` : 'Request declined');
        } else {
          const { value } = await Dialog.confirm({
            title: 'Cancel Request?',
            message: `Cancel your pending friend request to ${item.rawName}?`
          });
          if (value) {
            await this.game.removeFriendOnUDS(item.dnrId);
            await this.refreshData();
            this.renderActiveTab();
            this.showToast('Request cancelled');
          }
        }
      }
    });
  }

  renderFriendsTablesTab(width) {
    const refreshBtn = new GameButton({
      scene: this,
      x: 50,
      y: 235,
      w: 260,
      h: 65,
      text: 'Refresh Tables',
      font: '28px Arial',
      radius: 16,
      borderWidth: 3,
      addToScene: false,
      parentContainer: this.contentContainer,
      clickFunction: async () => {
        await this.refreshData();
        this.renderActiveTab();
      },
      clickFunctionData: {}
    });

    if (this.friendsTables.length === 0) {
      const panelBg = this.add.graphics();
      panelBg.fillStyle(0xffffff, 0.95);
      panelBg.fillRoundedRect(50, 315, width, 300, 14);
      this.contentContainer.add(panelBg);

      const emptyText = this.add.text(
        this.getWidth() / 2,
        465,
        'No friends are currently waiting at an open table.\nStart a Custom Game in Avalanche and invite them!',
        { font: '28px Arial', color: '#333333', align: 'center' }
      ).setOrigin(0.5);
      this.contentContainer.add(emptyText);
      return;
    }

    const items = this.friendsTables.map((t) => {
      const privacyLabel = t.privacy === 'friends' ? 'Friends Only' : 'Public';
      return {
        dnrId: t.id,
        avatar: 'mr_aces',
        label: `Host: ${t.host_name} (${t.id})\n${t.players}/${t.max_players} Players • ${privacyLabel}`
      };
    });

    this.activeScrollPanel = this.createGridPanel({
      items,
      x: 50,
      y: 315,
      width,
      height: this.getHeight() - 355,
      titleText: `Friends' Open Tables (${items.length}) — Tap to Join`,
      onChildClick: (child) => {
        if (child.name) {
          this.joinTableAndStart(child.name);
        }
      }
    });
  }

  joinTableAndStart(tableId) {
    this.game.pendingJoinTableId = tableId;
    this.closeScene();
    if (this.scene.isActive('GameSelection')) {
      this.scene.start('PlayGame', 'Avalanche').stop('GameSelection');
    } else if (this.scene.isActive('PlayGame')) {
      const pg = this.scene.get('PlayGame');
      if (pg) {
        pg.joinCustomTableById(tableId);
      }
    }
  }

  createGridPanel({ items, x, y, width, height, titleText, onChildClick }) {
    const columns = Math.max(1, Math.min(3, items.length));
    const rows = Math.max(1, Math.ceil(items.length / columns));

    const scrollablePanel = this.rexUI.add
      .scrollablePanel({
        x,
        y,
        width,
        height,
        scrollMode: 0,
        background: this.rexUI.add.roundRectangle(0, 0, 2, 2, 12, 0xffffff),
        panel: {
          child: this.rexUI.add
            .fixWidthSizer({
              space: { left: 12, right: 12, top: 15, bottom: 10, item: 12, line: 12 }
            })
            .enableLayoutWarn(false),
          width: width - 60,
          mask: { mask: true, padding: { right: -15 }, updateMode: 'everyTick' }
        },
        scroller: {},
        slider: {
          track: this.rexUI.add.roundRectangle(0, 0, 16, 10, 8, PlayGame.COLOR_LIGHT),
          thumb: this.rexUI.add.roundRectangle(0, 0, 0, 0, 16, PlayGame.COLOR_GOLD)
        },
        space: { left: 12, right: 12, top: 10, bottom: 10, panel: 10 }
      })
      .enableLayoutWarn(false)
      .setOrigin(0)
      .layout();

    const table = this.rexUI.add
      .gridSizer({
        column: columns,
        row: rows,
        rowProportions: 1,
        space: { column: 18, row: 18 }
      })
      .enableLayoutWarn(false);

    table.setChildrenInteractive().on('child.click', onChildClick, this);

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const c = i % columns;
      const r = Math.floor(i / columns);
      const label = this.rexUI.add
        .label({
          background: this.rexUI.add.roundRectangle(0, 0, 0, 0, 14, PlayGame.COLOR_GOLD),
          orientation: 'x',
          icon: item.avatar ? this.add.sprite(0, 0, item.avatar).setScale(0.32) : null,
          text: this.add.text(0, 0, item.label, { font: '24px Arial Black', color: '#000000' }),
          name: item.dnrId,
          space: { left: 16, right: 16, top: 12, bottom: 12, icon: 12 }
        })
        .enableLayoutWarn(false);
      table.add(label, c, r, 'top', 0, true);
    }

    const title = this.rexUI.add.label({
      width: width - 80,
      orientation: 'x',
      text: this.add.text(0, 0, titleText, { font: '28px Arial Black', color: '#000000' })
    });

    scrollablePanel.getElement('panel').add(title, 0, 'left', 0, true);
    scrollablePanel.getElement('panel').add(table, 1, 'left', 0, true);
    scrollablePanel.layout();
    return scrollablePanel;
  }
}
